// Floating text-formatting toolbar. Appears above the canvas when a text
// object (fabric.IText/Textbox) is selected; edits push directly to the
// active Fabric object and trigger a render.
import { useEffect, useState } from 'react'
import { fabric } from 'fabric'
import { isUppercaseText, toggleUppercaseText } from '../pages/designer/helpers'

interface Props {
  canvas: fabric.Canvas | null
  tick: number
}

const FONTS = [
  'Inter',
  'Barlow Condensed',
  'Impact',
  'Arial',
  'Arial Black',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Comic Sans MS',
  'Trebuchet MS',
  'Verdana',
]
const SIZES = [12, 16, 20, 24, 32, 40, 56, 72, 96, 128]

export default function TextToolbar({ canvas, tick }: Props) {
  const [obj, setObj] = useState<fabric.IText | null>(null)

  useEffect(() => {
    if (!canvas) { setObj(null); return }
    const active = canvas.getActiveObject()
    if (!active || (active.type !== 'i-text' && active.type !== 'text' && active.type !== 'textbox')) {
      setObj(null); return
    }
    setObj(active as fabric.IText)
  }, [canvas, tick])

  if (!obj) return null

  function apply(patch: Partial<fabric.IText>) {
    if (!obj || !canvas) return
    obj.set(patch as fabric.IObjectOptions)
    canvas.requestRenderAll()
    canvas.fire('object:modified', { target: obj })
  }

  const o = obj as fabric.IText & {
    fontFamily?: string; fontSize?: number; fontWeight?: string | number
    fontStyle?: string; fill?: string; underline?: boolean; linethrough?: boolean
    textAlign?: string; charSpacing?: number; lineHeight?: number
    stroke?: string; strokeWidth?: number; shadow?: fabric.Shadow | null
  }

  const charSpacing  = Math.round(o.charSpacing ?? 0)
  const lineHeight   = parseFloat((o.lineHeight ?? 1.16).toFixed(1))
  const strokeColor  = o.stroke && o.stroke !== '' ? o.stroke : '#000000'
  const strokeWidth  = o.strokeWidth ?? 0
  const hasShadow    = ((o.shadow as fabric.Shadow | null)?.blur ?? 0) > 0
  const isUppercase  = isUppercaseText(o.text ?? '')

  function toggleShadow() {
    if (!obj) return
    if (hasShadow) {
      apply({ shadow: undefined })
    } else {
      apply({ shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.45)', blur: 10, offsetX: 4, offsetY: 4 }) })
    }
  }

  function toggleUppercase() {
    if (!obj || !canvas || !obj.text?.trim()) return
    const next = toggleUppercaseText(obj.text!)
    obj.set({ text: next } as fabric.IObjectOptions)
    canvas.requestRenderAll()
    canvas.fire('object:modified', { target: obj })
  }

  const btn = (active: boolean) =>
    'w-7 h-7 rounded flex items-center justify-center transition-colors ' +
    (active ? 'bg-gold/15 text-gold-dark' : 'text-ink-2 hover:bg-surface')

  return (
    <div
      className="fixed z-[200] bg-white border border-border rounded-lg shadow-xl px-3 py-2 flex flex-col gap-2 text-xs"
      style={{ bottom: 80, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* ── Row 1: core formatting ── */}
      <div className="flex items-center gap-2">
        {/* Font family */}
        <select value={o.fontFamily ?? 'Inter'} onChange={(e) => apply({ fontFamily: e.target.value })}
          className="px-1.5 py-1 border border-border rounded bg-white text-ink min-w-[130px] focus:outline-none focus:border-gold">
          {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>

        {/* Font size */}
        <select value={Math.round(o.fontSize ?? 32)} onChange={(e) => apply({ fontSize: parseInt(e.target.value) })}
          className="px-1.5 py-1 border border-border rounded bg-white text-ink w-16 focus:outline-none focus:border-gold">
          {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <span className="w-px h-5 bg-border" />

        {/* Bold */}
        <button onClick={() => apply({ fontWeight: o.fontWeight === 'bold' ? 'normal' : 'bold' })}
          className={btn(o.fontWeight === 'bold') + ' font-bold'} title="Bold">B</button>
        {/* Italic */}
        <button onClick={() => apply({ fontStyle: o.fontStyle === 'italic' ? 'normal' : 'italic' })}
          className={btn(o.fontStyle === 'italic') + ' italic'} title="Italic">I</button>
        {/* Underline */}
        <button onClick={() => apply({ underline: !o.underline })}
          className={btn(!!o.underline) + ' underline'} title="Underline">U</button>
        {/* Strikethrough */}
        <button onClick={() => apply({ linethrough: !o.linethrough })}
          className={btn(!!o.linethrough) + ' line-through'} title="Strikethrough">S</button>

        <span className="w-px h-5 bg-border" />

        {/* Alignment */}
        {(['left','center','right'] as const).map((a) => (
          <button key={a} onClick={() => apply({ textAlign: a })}
            className={btn(o.textAlign === a)} title={`Align ${a}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              {a === 'left'   && <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></>}
              {a === 'center' && <><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></>}
              {a === 'right'  && <><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></>}
            </svg>
          </button>
        ))}

        <span className="w-px h-5 bg-border" />

        {/* Fill colour */}
        <label className="flex items-center gap-1 cursor-pointer" title="Text colour">
          <span className="text-ink-3">Fill</span>
          <input type="color" value={(o.fill as string | undefined) ?? '#0F1117'} onChange={(e) => apply({ fill: e.target.value })}
            className="w-7 h-7 p-0 border border-border rounded cursor-pointer" />
        </label>

        <span className="w-px h-5 bg-border" />

        {/* Delete */}
        <button
          onClick={() => {
            if (!obj || !canvas) return
            canvas.remove(obj); canvas.discardActiveObject()
            canvas.fire('object:modified', { target: null }); canvas.requestRenderAll()
          }}
          title="Delete text"
          className="w-7 h-7 rounded text-ink-2 hover:bg-red-50 hover:text-red-600 flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6M14 11v6"/>
          </svg>
        </button>
      </div>

      {/* ── Row 2: advanced formatting ── */}
      <div className="flex items-center gap-3 border-t border-border pt-1.5">

        {/* Letter spacing (tracking) */}
        <label className="flex items-center gap-1.5">
          <span className="text-ink-3 text-[10px] uppercase tracking-wide">Tracking</span>
          <input type="range" min={-200} max={800} step={10} value={charSpacing}
            onChange={(e) => apply({ charSpacing: parseInt(e.target.value) })}
            className="w-20 accent-gold" title={`Letter spacing: ${charSpacing}`} />
          <span className="w-8 text-center text-ink-3">{charSpacing}</span>
        </label>

        <span className="w-px h-5 bg-border" />

        {/* Line height (leading) */}
        <label className="flex items-center gap-1.5">
          <span className="text-ink-3 text-[10px] uppercase tracking-wide">Leading</span>
          <input type="range" min={0.8} max={3} step={0.1} value={lineHeight}
            onChange={(e) => apply({ lineHeight: parseFloat(e.target.value) })}
            className="w-16 accent-gold" title={`Line height: ${lineHeight}`} />
          <span className="w-6 text-center text-ink-3">{lineHeight}</span>
        </label>

        <span className="w-px h-5 bg-border" />

        {/* Text outline (stroke) */}
        <label className="flex items-center gap-1.5" title="Outline colour">
          <span className="text-ink-3 text-[10px] uppercase tracking-wide">Outline</span>
          <input type="color" value={strokeColor}
            onChange={(e) => apply({ stroke: e.target.value, strokeWidth: strokeWidth > 0 ? strokeWidth : 1 })}
            className="w-6 h-6 p-0 border border-border rounded cursor-pointer" />
          <input type="range" min={0} max={10} step={0.5} value={strokeWidth}
            onChange={(e) => apply({ strokeWidth: parseFloat(e.target.value), stroke: parseFloat(e.target.value) > 0 ? strokeColor : '' })}
            className="w-14 accent-gold" title={`Outline width: ${strokeWidth}`} />
          <span className="w-4 text-center text-ink-3">{strokeWidth}</span>
        </label>

        <span className="w-px h-5 bg-border" />

        {/* Shadow */}
        <button onClick={toggleShadow} className={btn(hasShadow) + ' gap-1 px-1.5 text-[10px] font-semibold w-auto'} title="Drop shadow">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <rect x="3" y="3" width="13" height="13" rx="2"/><path d="M8 21h13M21 8v13" strokeOpacity={0.4}/>
          </svg>
          Shadow
        </button>

        {/* Uppercase */}
        <button onClick={toggleUppercase} className={btn(isUppercase) + ' gap-1 px-1.5 text-[10px] font-bold w-auto'} title="Toggle uppercase">
          AA
        </button>

      </div>
    </div>
  )
}
